import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { createContext, useContext, useMemo } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { CustomerSupportContact } from '@model/CustomerSupportContact'

import { useAccountReadContext } from './AccountContextContext'
import { AuthSessionContext } from './AuthContext'
import { useProductReadContext } from './ProductContext'
import { useSessionLanguage } from './SessionSettingsContext'
import { useSignup } from './SignupContext'

interface CustomerSupportContextType {
  enabledContacts: CustomerSupportContact[]
  isLoading: boolean
}

const CustomerSupportContext = createContext<CustomerSupportContextType>({
  enabledContacts: [],
  isLoading: true,
})

export const CustomerSupportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()

  const [auth] = useContext(AuthSessionContext)
  const { signupData } = useSignup()
  const companyId = signupData.values.tickmillCompanyId || account?.tickmillCompany?.id || null
  const productId = auth ? product : signupData.values.productIds?.[0] || null

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: queryKeys.config.customerSupport(locale, companyId, productId),
    queryFn: () => apiClient.getCustomerSupportContacts(locale, companyId!, productId!),
    staleTime: Infinity,
    gcTime: 24 * 60 * 60_000,
    enabled: !!companyId && !!productId,
  })

  const enabledContacts = useMemo(() => contacts.filter((contact) => contact.enabled), [contacts])

  return (
    <CustomerSupportContext.Provider value={{ enabledContacts, isLoading }}>
      {children}
    </CustomerSupportContext.Provider>
  )
}

export const useCustomerSupport = (): CustomerSupportContextType =>
  useContext(CustomerSupportContext)
